import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Calendar } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import WaitlistDialog from "../components/WaitlistDialog";
import Hero from "../components/Hero";
import ROIStrip from "../components/ROIStrip";
import PainVsCleany from "../components/PainVsCleany";
import TrustBadges from "../components/TrustBadges";
import BookDemoBlock from "../components/BookDemoBlock";
import Pricing from "../components/Pricing";
import FAQ from "../components/FAQ";

const CAL_URL = "https://cal.com/cleany-dk/20min";

/**
 * ForsideV2Page — a trimmed variant of the homepage aimed at paid traffic.
 * Reuses the exact same components as `/` (Hero, ROIStrip, PainVsCleany,
 * TrustBadges, BookDemoBlock, Pricing, FAQ) so the visual design and layout
 * matches the homepage 1:1. Only the section list and CTAs are different:
 *   - Hero CTAs are "Prøv 14 dage gratis" + "Beregn pris" (instead of waitlist)
 *   - Removed: TrustBar, Personas, Comparison, Modules, MobileShowcase,
 *     Countdown, HowItWorks, LatestUpdatesStrip, About, ContactSection
 *   - Kept: Hero, ROIStrip, PainVsCleany, TrustBadges, BookDemoBlock, Pricing, FAQ
 *   - Added: One extra dual-CTA block after Pricing to reinforce trial + demo
 */
export default function ForsideV2Page() {
    const [waitlistOpen, setWaitlistOpen] = useState(false);

    useEffect(() => {
        document.title = "Cleany · Driftsplatform for danske rengøringsfirmaer";
        const robots =
            document.querySelector("meta[name='robots']") ||
            (() => {
                const m = document.createElement("meta");
                m.name = "robots";
                document.head.appendChild(m);
                return m;
            })();
        robots.setAttribute("content", "noindex, nofollow");
        return () => robots.setAttribute("content", "index, follow");
    }, []);

    return (
        <div className="min-h-screen bg-white" data-testid="forside-v2">
            <Navbar onCtaClick={() => setWaitlistOpen(true)} />

            <main className="pb-20 md:pb-0">
                {/* Hero with custom dual CTA */}
                <Hero
                    onCtaClick={() => setWaitlistOpen(true)}
                    primaryCta={{
                        href: "/14-dages-proeve",
                        label: "Prøv 14 dage gratis",
                        testid: "hero-v2-cta-trial",
                    }}
                    secondaryCta={{
                        href: "/beregn-pris",
                        label: "Beregn pris",
                        testid: "hero-v2-cta-calc",
                    }}
                />

                {/* Konkret værdi */}
                <ROIStrip onCtaClick={() => { window.location.href = "/14-dages-proeve"; }} />

                {/* Hvorfor Cleany */}
                <PainVsCleany />

                {/* Sikkerhed & compliance */}
                <TrustBadges />

                {/* Book demo */}
                <BookDemoBlock />

                {/* Priser (uden beregner) */}
                <Pricing hideCalculator onCtaClick={() => { window.location.href = "/14-dages-proeve"; }} />

                {/* Ekstra dual-CTA block før FAQ */}
                <section className="pb-20 pt-4" data-testid="v2-final-cta">
                    <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
                        <div className="rounded-2xl bg-sky-50/60 border border-sky-100 p-8 md:p-10">
                            <h3 className="font-display text-2xl md:text-3xl font-bold text-slate-900 leading-tight">
                                Klar til at prøve det på jeres egne data?
                            </h3>
                            <p className="mt-3 text-slate-600 leading-relaxed max-w-xl mx-auto">
                                Start jeres 14-dages prøve i dag — eller book en 20 min. demo hvis I vil se det live først.
                            </p>
                            <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
                                <Link
                                    to="/14-dages-proeve"
                                    data-testid="v2-final-cta-trial"
                                    className="inline-flex items-center justify-center gap-2 rounded-full bg-sky-600 hover:bg-sky-700 text-white font-semibold px-6 h-12 shadow-lg shadow-sky-600/20 transition-all active:scale-[0.97]"
                                >
                                    Prøv 14 dage gratis
                                    <ArrowRight className="h-4 w-4" />
                                </Link>
                                <a
                                    href={CAL_URL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="v2-final-cta-demo"
                                    className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-300 bg-white hover:border-slate-400 text-slate-800 font-semibold px-6 h-12 transition-colors"
                                >
                                    <Calendar className="h-4 w-4" /> Book demo
                                </a>
                            </div>
                        </div>
                    </div>
                </section>

                {/* FAQ */}
                <FAQ />
            </main>

            <Footer onCtaClick={() => setWaitlistOpen(true)} />
            <WaitlistDialog open={waitlistOpen} onClose={() => setWaitlistOpen(false)} source="forside-v2" />
        </div>
    );
}
